import React, {useEffect, useRef, useState, useCallback, useMemo} from 'react';
import {View, Text, StyleSheet} from 'react-native';
import {
  useDataChannel,
  useRemoteParticipants,
  useSpeakingParticipants,
  useLocalParticipant,
  useTracks,
} from '@livekit/components-react';
import {RoomEvent, Track} from 'livekit-client';
import {TranscriptionTile} from './TranscriptionTile';
import LottieView from 'react-native-web-lottie';
import {useSelector} from 'react-redux';
import LiveKitTextInput from './LiveKitTextInput';

export default function LiveKitSDK() {
  const tracks = useTracks();
  const participants = useRemoteParticipants({
    updateOnlyOn: [RoomEvent.ParticipantMetadataChanged],
  });
  const agentParticipant = participants.find(p => p.isAgent);
  const {localParticipant} = useLocalParticipant();
  const speakingParticipants = useSpeakingParticipants();

  const oneToOneMeetingStatus = useSelector(
    state => state.communication.speechToTextService.oneToOneMeetingStatus,
  );

  // who’s speaking right now?
  const isAgentSpeaking = agentParticipant
    ? speakingParticipants.some(p => p.identity === agentParticipant.identity)
    : false;
  const isUserSpeaking = speakingParticipants.some(
    p => p.identity === localParticipant.identity,
  );

  // debounce window (ms) before we declare “agent finished”
  const DEBOUNCE_MS = 2000;
  const prevAgentRef = useRef(false);
  const agentTimeout = useRef();

  // overlay state: show whenever agent finishes, hide on user speak
  const [enableInput, setEnableInput] = useState(false);

  // detect agent start/finish
  useEffect(() => {
    if (isAgentSpeaking && !prevAgentRef.current) {
      // agent just started
      prevAgentRef.current = true;
      clearTimeout(agentTimeout.current);
    }
    if (!isAgentSpeaking && prevAgentRef.current) {
      // agent just stopped → debounce before showing overlay
      clearTimeout(agentTimeout.current);
      agentTimeout.current = setTimeout(() => {
        if (!isAgentSpeaking) {
          if (oneToOneMeetingStatus) {
            setEnableInput(true);
          }
        }
      }, DEBOUNCE_MS);
      prevAgentRef.current = false;
    }
    return () => clearTimeout(agentTimeout.current);
  }, [isAgentSpeaking]);

  // hide overlay as soon as user speaks
  useEffect(() => {
    if (isUserSpeaking || isAgentSpeaking || !oneToOneMeetingStatus) {
      setEnableInput(false);
    }
  }, [isUserSpeaking, isAgentSpeaking, oneToOneMeetingStatus]);

  // cleanup on unmount
  useEffect(() => {
    return () => clearTimeout(agentTimeout.current);
  }, []);

  // — your transcription + data‐channel logic unchanged —
  const aat = tracks.find(
    t => t.publication.kind === Track.Kind.Audio && t.participant.isAgent,
  );
  let agentAudioTrack;
  if (aat) agentAudioTrack = aat;
  else if (agentParticipant) {
    agentAudioTrack = {
      participant: agentParticipant,
      source: Track.Source.Microphone,
    };
  }

  const chatTileContent = useMemo(() => {
    if (!agentAudioTrack) return null;
    return (
      <TranscriptionTile
        agentAudioTrack={agentAudioTrack}
        accentColor="#FF0000"
      />
    );
  }, [agentAudioTrack]);

  const onDataReceived = useCallback(msg => {
    console.log('onDataReceived', msg);
  }, []);
  useDataChannel(e => console.log('useDataChannel', e));

  return (
    <>
      {chatTileContent}
      {oneToOneMeetingStatus &&
      <LiveKitTextInput disabled={!enableInput}  onSent={() => setEnableInput(false)}/>}


      {enableInput && oneToOneMeetingStatus && (
        <View style={styles.overlayContainer}>
          <View style={styles.overlayBox}>
            <LottieView
              source={require('../../utils/animations/mic-animation.json')}
              autoPlay
              loop
              style={styles.lottie}
            />
            <Text style={styles.overlayText}>Speak or Type below to Respond</Text>
          </View>
        </View>
      )}
    </>
  );
}

const LOTTIE_SIZE = 60;
const PADDING =20;
const BACKGROUND_ALPHA = 0.5; // 0 = transparent, 1 = opaque
const BOX_WIDTH = LOTTIE_SIZE + PADDING * 6;

const styles = StyleSheet.create({
  // full-screen flex container to perfectly center the box
  overlayContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 250,
    justifyContent: 'center',
    alignItems: 'center',
    pointerEvents: 'none',
    zIndex: 10,
  },
  // the centered box around your animation + text
  overlayBox: {
    width: BOX_WIDTH,
    padding: PADDING,
    backgroundColor: `rgba(0, 0, 0, ${BACKGROUND_ALPHA})`,
    borderRadius: 16,
    alignItems: 'center',
  },
  lottie: {
    width: LOTTIE_SIZE,
    height: LOTTIE_SIZE,
  },
  overlayText: {
    marginTop: 8,
    color: '#BDDE36',
    fontWeight: 'bold',
    fontSize: 14,
    textAlign: 'center',
  },
});
